import React, { useState } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
interface WorkoutCreationFlowProps {
  navigateTo: (screen: string) => void;
  onBack: () => void;
}
export const WorkoutCreationFlow = ({
  navigateTo,
  onBack
}: WorkoutCreationFlowProps) => {
  const [step, setStep] = useState(0);
  const [workoutType, setWorkoutType] = useState<'oneoff' | 'longterm' | null>(null);
  // One-off workout parameters
  const [oneOffParams, setOneOffParams] = useState({
    time: '30',
    energy: 'medium',
    location: 'home'
  });
  // Long-term plan parameters
  const [longTermParams, setLongTermParams] = useState({
    goal: '',
    duration: '',
    frequency: [],
    workoutTypes: []
  });
  const nextStep = () => {
    setStep(step + 1);
  };
  const prevStep = () => {
    if (step === 0) {
      onBack();
    } else {
      setStep(step - 1);
    }
  };
  const updateOneOffParams = (field: string, value: string) => {
    setOneOffParams({
      ...oneOffParams,
      [field]: value
    });
  };
  const updateLongTermParams = (field: string, value: any) => {
    setLongTermParams({
      ...longTermParams,
      [field]: value
    });
  };
  const toggleWorkoutType = (type: string) => {
    const currentTypes = [...longTermParams.workoutTypes];
    if (currentTypes.includes(type)) {
      updateLongTermParams('workoutTypes', currentTypes.filter(t => t !== type));
    } else {
      updateLongTermParams('workoutTypes', [...currentTypes, type]);
    }
  };
  const toggleFrequency = (day: string) => {
    const currentFrequency = [...longTermParams.frequency];
    if (currentFrequency.includes(day)) {
      updateLongTermParams('frequency', currentFrequency.filter(d => d !== day));
    } else {
      updateLongTermParams('frequency', [...currentFrequency, day]);
    }
  };
  const createWorkout = () => {
    // Generate workout data based on the type of workout
    const workoutData = workoutType === 'oneoff' ? {
      title: `${oneOffParams.energy.charAt(0).toUpperCase() + oneOffParams.energy.slice(1)} Intensity ${oneOffParams.location === 'gym' ? 'Gym' : 'Home'} Workout`,
      duration: oneOffParams.time,
      location: oneOffParams.location,
      energy: oneOffParams.energy,
      exercises: [{
        name: 'Warm-up',
        duration: '5 min',
        type: 'warmup'
      }, {
        name: 'Dynamic Stretching',
        duration: '3 min',
        type: 'warmup'
      }, {
        name: 'Main Circuit',
        duration: `${parseInt(oneOffParams.time) - 10} min`,
        type: 'main',
        exercises: [{
          name: 'Push-ups',
          sets: 3,
          reps: 12,
          rest: '30 sec'
        }, {
          name: 'Squats',
          sets: 3,
          reps: 15,
          rest: '30 sec'
        }, {
          name: 'Plank',
          sets: 3,
          duration: '30 sec',
          rest: '30 sec'
        }, {
          name: 'Mountain Climbers',
          sets: 3,
          duration: '45 sec',
          rest: '30 sec'
        }]
      }, {
        name: 'Cool Down',
        duration: '5 min',
        type: 'cooldown'
      }]
    } : {
      title: `${longTermParams.goal === '5k' ? '5K' : longTermParams.goal === '10k' ? '10K' : longTermParams.goal} Training Plan - Week 1`,
      duration: '45',
      frequency: longTermParams.frequency,
      workoutTypes: longTermParams.workoutTypes,
      plan: `${longTermParams.duration.split('-')[0]}-week plan`,
      exercises: [{
        name: 'Warm-up',
        duration: '5 min',
        type: 'warmup'
      }, {
        name: 'Dynamic Stretching',
        duration: '5 min',
        type: 'warmup'
      }, {
        name: 'Main Workout',
        duration: '30 min',
        type: 'main',
        exercises: [{
          name: 'Running Intervals',
          sets: 5,
          duration: '3 min',
          rest: '1 min'
        }, {
          name: 'Strength Circuit',
          sets: 3,
          exercises: [{
            name: 'Lunges',
            reps: 12,
            rest: '30 sec'
          }, {
            name: 'Push-ups',
            reps: 10,
            rest: '30 sec'
          }, {
            name: 'Plank',
            duration: '45 sec',
            rest: '30 sec'
          }]
        }]
      }, {
        name: 'Cool Down',
        duration: '5 min',
        type: 'cooldown'
      }]
    };
    // Navigate to the workout overview screen with the workout data
    navigateTo('workout-overview', workoutData);
  };
  const renderStep = () => {
    switch (step) {
      case 0:
        return <div className="p-6 flex flex-col h-full" data-id="element-1008">
            <div className="mb-6" data-id="element-1009">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1010">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1011">
                What would you like to create?
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-1012">
                Choose between a single workout or a comprehensive plan.
              </p>
            </div>
            <div className="space-y-4 mb-8" data-id="element-1013">
              <Card onClick={() => {
              setWorkoutType('oneoff');
              nextStep();
            }} active={workoutType === 'oneoff'} className="p-5" data-id="element-1014">
                <div className="flex items-start" data-id="element-1015">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-500 flex-shrink-0" data-id="element-1016">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1017">
                      <path d="M18 8h1a4 4 0 0 1 0 8h-1" data-id="element-1018"></path>
                      <path d="M6 8h-1a4 4 0 0 0 0 8h1" data-id="element-1019"></path>
                      <path d="M8 8v8" data-id="element-1020"></path>
                      <path d="M16 8v8" data-id="element-1021"></path>
                      <path d="M12 8v8" data-id="element-1022"></path>
                    </svg>
                  </div>
                  <div data-id="element-1023">
                    <h3 className="font-bold text-lg mb-1" data-id="element-1024">
                      One-off Smart Session
                    </h3>
                    <p className="text-gray-600" data-id="element-1025">
                      Build me something for today based on my time, energy
                      level, and location.
                    </p>
                  </div>
                </div>
              </Card>
              <Card onClick={() => {
              setWorkoutType('longterm');
              nextStep();
            }} active={workoutType === 'longterm'} className="p-5" data-id="element-1026">
                <div className="flex items-start" data-id="element-1027">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4 text-green-500 flex-shrink-0" data-id="element-1028">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1029">
                      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" data-id="element-1030"></rect>
                      <line x1="3" x2="21" y1="9" y2="9" data-id="element-1031"></line>
                      <line x1="9" x2="9" y1="21" y2="9" data-id="element-1032"></line>
                    </svg>
                  </div>
                  <div data-id="element-1033">
                    <h3 className="font-bold text-lg mb-1" data-id="element-1034">Long-Term Plan</h3>
                    <p className="text-gray-600" data-id="element-1035">
                      Create a comprehensive training plan for a specific goal
                      with multiple workout types.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>;
      // One-off workout flow
      case 1:
        if (workoutType === 'oneoff') {
          return <div className="p-6 flex flex-col h-full" data-id="element-1036">
              <div className="mb-6" data-id="element-1037">
                <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1038">
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1039">
                  Create a workout for today
                </h1>
                <p className="text-gray-600 mb-6" data-id="element-1040">
                  Tell us about your preferences for today's session.
                </p>
              </div>
              <div className="space-y-6 mb-8" data-id="element-1041">
                <div data-id="element-1042">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1043">
                    How much time do you have?
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-1044">
                    {['15', '30', '45', '60'].map(time => <button key={time} onClick={() => updateOneOffParams('time', time)} className={`py-2 rounded-lg text-center ${oneOffParams.time === time ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-1045">
                        {time} min
                      </button>)}
                  </div>
                </div>
                <div data-id="element-1046">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1047">
                    What's your energy level today?
                  </label>
                  <div className="grid grid-cols-3 gap-3" data-id="element-1048">
                    {[{
                    value: 'low',
                    label: 'Low',
                    emoji: '🌙'
                  }, {
                    value: 'medium',
                    label: 'Medium',
                    emoji: '🔆'
                  }, {
                    value: 'high',
                    label: 'High',
                    emoji: '⚡'
                  }].map(energy => <button key={energy.value} onClick={() => updateOneOffParams('energy', energy.value)} className={`py-3 rounded-lg flex flex-col items-center ${oneOffParams.energy === energy.value ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-1049">
                        <span className="text-xl mb-1" data-id="element-1050">{energy.emoji}</span>
                        <span data-id="element-1051">{energy.label}</span>
                      </button>)}
                  </div>
                </div>
                <div data-id="element-1052">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1053">
                    Where will you be working out?
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1054">
                    {[{
                    value: 'home',
                    label: 'At Home',
                    icon: '🏠'
                  }, {
                    value: 'gym',
                    label: 'At the Gym',
                    icon: '💪'
                  }].map(location => <button key={location.value} onClick={() => updateOneOffParams('location', location.value)} className={`py-4 rounded-lg flex flex-col items-center ${oneOffParams.location === location.value ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-1055">
                        <span className="text-2xl mb-1" data-id="element-1056">{location.icon}</span>
                        <span data-id="element-1057">{location.label}</span>
                      </button>)}
                  </div>
                </div>
              </div>
              <div className="mt-auto" data-id="element-1058">
                <Button onClick={nextStep} variant="primary" fullWidth data-id="element-1059">
                  Create My Workout
                </Button>
              </div>
            </div>;
        } else if (workoutType === 'longterm') {
          return <div className="p-6 flex flex-col h-full" data-id="element-1060">
              <div className="mb-6" data-id="element-1061">
                <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1062">
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1063">
                  What's your training goal?
                </h1>
                <p className="text-gray-600 mb-6" data-id="element-1064">
                  Select a goal for your long-term training plan.
                </p>
              </div>
              <div className="space-y-4 mb-8" data-id="element-1065">
                {[{
                value: '5k',
                label: 'Run a 5K',
                icon: '🏃‍♀️',
                description: 'Build endurance for a 5K run (3.1 miles)'
              }, {
                value: '10k',
                label: 'Train for a 10K',
                icon: '🏃‍♂️',
                description: 'Prepare for a 10K race (6.2 miles)'
              }, {
                value: 'strength',
                label: 'Build Strength',
                icon: '💪',
                description: 'Focus on muscle building and strength gains'
              }, {
                value: 'weight-loss',
                label: 'Weight Loss',
                icon: '⚖️',
                description: 'Structured plan for sustainable weight loss'
              }, {
                value: 'custom',
                label: 'Custom Goal',
                icon: '📅',
                description: 'Create a plan for your specific needs'
              }].map(goal => <Card key={goal.value} onClick={() => updateLongTermParams('goal', goal.value)} active={longTermParams.goal === goal.value} className="p-4" data-id="element-1066">
                    <div className="flex items-center" data-id="element-1067">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-2xl" data-id="element-1068">
                        {goal.icon}
                      </div>
                      <div data-id="element-1069">
                        <h3 className="font-medium text-gray-900" data-id="element-1070">
                          {goal.label}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-1071">
                          {goal.description}
                        </p>
                      </div>
                    </div>
                  </Card>)}
              </div>
              <div className="mt-auto" data-id="element-1072">
                <Button onClick={nextStep} variant="primary" fullWidth disabled={!longTermParams.goal} data-id="element-1073">
                  Continue
                </Button>
              </div>
            </div>;
        }
        return null;
      case 2:
        if (workoutType === 'oneoff') {
          // One-off workout results
          return <div className="p-6 flex flex-col h-full" data-id="element-1074">
              <div className="mb-6" data-id="element-1075">
                <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1076">
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1077">
                  Your Workout is Ready!
                </h1>
                <p className="text-gray-600 mb-6" data-id="element-1078">
                  We've created a {oneOffParams.time}-minute{' '}
                  {oneOffParams.energy} intensity workout for{' '}
                  {oneOffParams.location === 'home' ? 'home' : 'the gym'}.
                </p>
              </div>
              <div className="bg-blue-50 rounded-xl p-5 mb-6" data-id="element-1079">
                <h2 className="font-semibold text-blue-800 mb-3" data-id="element-1080">
                  {oneOffParams.energy === 'high' ? 'High-Intensity' : oneOffParams.energy === 'medium' ? 'Moderate' : 'Low-Impact'}{' '}
                  {oneOffParams.location === 'gym' ? 'Gym' : 'Home'} Workout
                </h2>
                <div className="bg-white rounded-lg p-4 mb-4" data-id="element-1081">
                  <div className="flex justify-between items-center mb-2" data-id="element-1082">
                    <h3 className="font-medium" data-id="element-1083">Workout Summary</h3>
                    <span className="text-blue-500 text-sm" data-id="element-1084">
                      {oneOffParams.time} min
                    </span>
                  </div>
                  <ul className="space-y-3" data-id="element-1085">
                    <li className="flex items-center" data-id="element-1086">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-blue-500 text-sm" data-id="element-1087">
                        1
                      </div>
                      <span data-id="element-1088">Warm-up - 5 min</span>
                    </li>
                    <li className="flex items-center" data-id="element-1089">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-blue-500 text-sm" data-id="element-1090">
                        2
                      </div>
                      <span data-id="element-1091">
                        Main circuit - {parseInt(oneOffParams.time) - 10} min
                      </span>
                    </li>
                    <li className="flex items-center" data-id="element-1092">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-blue-500 text-sm" data-id="element-1093">
                        3
                      </div>
                      <span data-id="element-1094">Cool down - 5 min</span>
                    </li>
                  </ul>
                </div>
                <p className="text-sm text-blue-700" data-id="element-1095">
                  This workout is designed for your current energy level and
                  available equipment. You can save it to your library or start
                  right away.
                </p>
              </div>
              <div className="mt-auto space-y-4" data-id="element-1096">
                <Button onClick={createWorkout} variant="primary" fullWidth data-id="element-1097">
                  Start Workout
                </Button>
                <Button onClick={createWorkout} variant="outline" fullWidth data-id="element-1098">
                  Save to My Workouts
                </Button>
              </div>
            </div>;
        } else if (workoutType === 'longterm') {
          // Long-term plan duration selection
          return <div className="p-6 flex flex-col h-full" data-id="element-1099">
              <div className="mb-6" data-id="element-1100">
                <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1101">
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1102">
                  How long do you want to train?
                </h1>
                <p className="text-gray-600 mb-6" data-id="element-1103">
                  Choose a duration for your{' '}
                  {longTermParams.goal === '5k' ? '5K' : longTermParams.goal === '10k' ? '10K' : longTermParams.goal}{' '}
                  training plan.
                </p>
              </div>
              <div className="space-y-4 mb-8" data-id="element-1104">
                {[{
                value: '4-weeks',
                label: '4 Weeks',
                description: 'Quick progress for those with some experience'
              }, {
                value: '8-weeks',
                label: '8 Weeks',
                description: 'Balanced approach for steady improvement'
              }, {
                value: '12-weeks',
                label: '12 Weeks',
                description: 'Comprehensive plan with gradual progression'
              }, {
                value: 'custom',
                label: '📅 Custom Duration',
                description: 'Set your own timeline for training'
              }].map(duration => <Card key={duration.value} onClick={() => updateLongTermParams('duration', duration.value)} active={longTermParams.duration === duration.value} className="p-4" data-id="element-1105">
                    <div className="flex items-center" data-id="element-1106">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-500 font-medium" data-id="element-1107">
                        {duration.value.split('-')[0]}
                      </div>
                      <div data-id="element-1108">
                        <h3 className="font-medium text-gray-900" data-id="element-1109">
                          {duration.label}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-1110">
                          {duration.description}
                        </p>
                      </div>
                    </div>
                  </Card>)}
              </div>
              <div className="mt-auto" data-id="element-1111">
                <Button onClick={nextStep} variant="primary" fullWidth disabled={!longTermParams.duration} data-id="element-1112">
                  Continue
                </Button>
              </div>
            </div>;
        }
        return null;
      case 3:
        if (workoutType === 'longterm') {
          // Workout frequency selection
          return <div className="p-6 flex flex-col h-full" data-id="element-1113">
              <div className="mb-6" data-id="element-1114">
                <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1115">
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1116">
                  Which days can you train?
                </h1>
                <p className="text-gray-600 mb-6" data-id="element-1117">
                  Select the days you're available for workouts.
                </p>
              </div>
              <div className="space-y-3 mb-8" data-id="element-1118">
                {[{
                value: 'monday',
                label: 'Monday'
              }, {
                value: 'tuesday',
                label: 'Tuesday'
              }, {
                value: 'wednesday',
                label: 'Wednesday'
              }, {
                value: 'thursday',
                label: 'Thursday'
              }, {
                value: 'friday',
                label: 'Friday'
              }, {
                value: 'saturday',
                label: 'Saturday'
              }, {
                value: 'sunday',
                label: 'Sunday'
              }].map(day => <div key={day.value} onClick={() => toggleFrequency(day.value)} className={`p-4 rounded-lg flex items-center justify-between cursor-pointer ${longTermParams.frequency.includes(day.value) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-1119">
                    <span data-id="element-1120">{day.label}</span>
                    {longTermParams.frequency.includes(day.value) && <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1121">
                        <path d="M20 6 9 17l-5-5" data-id="element-1122"></path>
                      </svg>}
                  </div>)}
              </div>
              <div className="mt-auto" data-id="element-1123">
                <Button onClick={nextStep} variant="primary" fullWidth disabled={longTermParams.frequency.length === 0} data-id="element-1124">
                  Continue
                </Button>
              </div>
            </div>;
        }
        return null;
      case 4:
        if (workoutType === 'longterm') {
          // Workout types selection
          return <div className="p-6 flex flex-col h-full" data-id="element-1125">
              <div className="mb-6" data-id="element-1126">
                <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1127">
                  ← Back
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1128">
                  What types of workouts do you want?
                </h1>
                <p className="text-gray-600 mb-6" data-id="element-1129">
                  Select the workout types to include in your plan.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1130">
                {[{
                value: 'strength',
                label: 'Strength',
                icon: '💪',
                description: 'Build muscle and power'
              }, {
                value: 'cardio',
                label: 'Cardio',
                icon: '❤️',
                description: 'Improve endurance'
              }, {
                value: 'hiit',
                label: 'HIIT',
                icon: '⚡',
                description: 'High intensity intervals'
              }, {
                value: 'yoga',
                label: 'Yoga',
                icon: '🧘',
                description: 'Flexibility and recovery'
              }, {
                value: 'run',
                label: 'Running',
                icon: '🏃',
                description: 'Outdoor or treadmill'
              }, {
                value: 'core',
                label: 'Core',
                icon: '🔄',
                description: 'Focus on abdominals'
              }].map(type => <div key={type.value} onClick={() => toggleWorkoutType(type.value)} className={`p-4 rounded-lg flex flex-col items-center text-center cursor-pointer ${longTermParams.workoutTypes.includes(type.value) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-1131">
                    <span className="text-2xl mb-1" data-id="element-1132">{type.icon}</span>
                    <span className="font-medium" data-id="element-1133">{type.label}</span>
                    <span className="text-xs mt-1" data-id="element-1134">
                      {longTermParams.workoutTypes.includes(type.value) ? 'Selected' : type.description}
                    </span>
                  </div>)}
              </div>
              <div className="mt-auto" data-id="element-1135">
                <Button onClick={nextStep} variant="primary" fullWidth disabled={longTermParams.workoutTypes.length === 0} data-id="element-1136">
                  Create My Plan
                </Button>
              </div>
            </div>;
        }
        return null;
      case 5:
        // Long-term plan results
        return <div className="p-6 flex flex-col h-full" data-id="element-1137">
            <div className="mb-6" data-id="element-1138">
              <button onClick={prevStep} className="text-gray-500 mb-4" data-id="element-1139">
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1140">
                Your Training Plan is Ready!
              </h1>
              <p className="text-gray-600 mb-6" data-id="element-1141">
                We've created a {longTermParams.duration.split('-')[0]}-week
                plan to help you achieve your{' '}
                {longTermParams.goal === '5k' ? '5K' : longTermParams.goal === '10k' ? '10K' : longTermParams.goal}{' '}
                goal.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-5 mb-6" data-id="element-1142">
              <h2 className="font-semibold text-blue-800 mb-3" data-id="element-1143">Plan Summary</h2>
              <div className="bg-white rounded-lg p-4 mb-4" data-id="element-1144">
                <h3 className="font-medium mb-3" data-id="element-1145">Weekly Schedule</h3>
                <div className="space-y-2" data-id="element-1146">
                  {longTermParams.frequency.map((day, index) => {
                  const workoutType = longTermParams.workoutTypes[index % longTermParams.workoutTypes.length];
                  return <div key={day} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg" data-id="element-1147">
                        <span className="capitalize" data-id="element-1148">{day}</span>
                        <span className="text-blue-500 capitalize" data-id="element-1149">
                          {workoutType}
                        </span>
                      </div>;
                })}
                </div>
              </div>
              <div className="bg-green-100 rounded-lg p-4 mb-4" data-id="element-1150">
                <div className="flex items-start" data-id="element-1151">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 mr-2 mt-0.5" data-id="element-1152">
                    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" data-id="element-1153"></path>
                    <path d="m9 12 2 2 4-4" data-id="element-1154"></path>
                  </svg>
                  <div data-id="element-1155">
                    <h3 className="font-medium text-green-800 mb-1" data-id="element-1156">
                      Adaptive Planning
                    </h3>
                    <p className="text-sm text-green-700" data-id="element-1157">
                      Life happens — if you miss a workout, we'll automatically
                      adjust your plan to keep you on track.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-blue-700" data-id="element-1158">
                Your plan includes {longTermParams.workoutTypes.length}{' '}
                different workout types and gradually increases in intensity as
                you progress.
              </p>
            </div>
            <div className="mt-auto space-y-4" data-id="element-1159">
              <Button onClick={createWorkout} variant="primary" fullWidth data-id="element-1160">
                Start My Plan
              </Button>
              <Button onClick={prevStep} variant="outline" fullWidth data-id="element-1161">
                Modify Plan
              </Button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="flex flex-col h-full min-h-screen bg-white" data-id="element-1162">
      {renderStep()}
    </div>;
};